import React from 'react';
import { eventChannel } from 'redux-saga';
import { call, put, select, spawn, takeEvery, takeLatest } from 'redux-saga/effects';
import DOMService from 'mangools-commons/dist/services/DOMService';
import ColorSchemes from 'mangools-commons/dist/constants/ColorSchemes';

import ActionTypes from 'constants/ActionTypes';
import { analyticsEvents } from 'constants/analytics';

import { filteredAndSortedResultsDataSelector } from 'selectors/dataSelectors';

import {
    receivedErrorNotificationAction,
    receivedInfoNotificationAction,
    requestedCloseAllNotificationsAction,
    setColorScheme,
    setOnlineStatus,
    setSelectedKeywords,
    setSortingSettings,
} from 'actions/uiActions';

import { gtmTrack } from 'actions/analyticsActions';

import { colorSchemeSelector, onlineStatusSelector } from 'selectors/uiSelectors';
import { toast } from 'react-toastify';
import { isCompetitorKeywordsSelector } from 'selectors/dataSelectors';
import { requestedKeywordsByDomainResultsAction } from 'actions/dataActions';
import { setParams } from 'actions/paramsActions';
import { paramsSelector } from 'selectors/paramsSelectors';
import DataSubSourceTypes from 'constants/DataSubSourceTypes';
import { COMPETITOR_SORTABLE_COLUMNS_ONLY_ON_FE } from 'constants/keywordColumns';
import { defaultCompetitorQuerySelector } from 'selectors/defaultsSelectors';

// EXPORTS
export function* updateSortingSettingsSaga(action) {
    const isCompetitorKeywords = yield select(isCompetitorKeywordsSelector);
    yield put(setSortingSettings(action.payload.column, action.payload.type));

    if (!isCompetitorKeywords) {
        return;
    }

    if (COMPETITOR_SORTABLE_COLUMNS_ONLY_ON_FE.includes(action.payload.column)) {
        return;
    }
    const competitorQuery = yield select(defaultCompetitorQuerySelector);
    const params = yield select(paramsSelector);
    const newParams = {
        ...params,
        orderBy: action.payload.column,
        orderDirection: action.payload.type,
        query: competitorQuery,
    };
    yield put(setParams(newParams));
    yield put(requestedKeywordsByDomainResultsAction(DataSubSourceTypes.COMPETITORS));
}

export function* showErrorNotification(message, config = {}) {
    yield put(receivedErrorNotificationAction(message, config));
    yield call(toast.error, message, config);
}

export function* showInfoNotification(message, config = {}) {
    yield put(receivedInfoNotificationAction(message, config));
    yield call(toast.info, message, config);
}

export function* closeAllNotifications() {
    yield put(requestedCloseAllNotificationsAction());
    yield call(toast.dismiss);
}

// WORKERS

function* selectAllKeywords() {
    const keywords = yield select(filteredAndSortedResultsDataSelector);
    yield put(setSelectedKeywords(keywords.map(kw => kw.id)));
}

function* updateOnlineStatus() {
    const currentStatus = yield select(onlineStatusSelector);
    const newStatus = window.navigator.onLine;
    yield put(setOnlineStatus({ onlineStatus: newStatus }));

    if (currentStatus === true && newStatus === false) {
        yield call(closeAllNotifications);
        // NOTE: Went from online to offline, show error notification
        yield call(
            showErrorNotification,
            <div>
                You have lost your <strong>internet connection</strong>!
            </div>,
        );
    } else if (currentStatus === false && newStatus === true) {
        yield call(closeAllNotifications);
        // NOTE: Went from offline to online, show success notification
        yield call(
            showInfoNotification,
            <div>
                You are now <strong>online</strong>!
            </div>,
        );
    }
}

function* updateColorSchemeFromChannel(channel) {
    const colorScheme = yield select(colorSchemeSelector);

    if (colorScheme === ColorSchemes.AUTO) {
        if (DOMService.isDarkMode(channel)) {
            DOMService.darkModeOn();
        } else {
            DOMService.darkModeOff();
        }

        // NOTE: this triggers force rerender in components to check for dark mode
        yield put(setColorScheme(ColorSchemes.LIGHT));
        yield put(setColorScheme(ColorSchemes.AUTO));
    }
}

function* updateColorScheme({ payload }) {
    if (DOMService.isDarkMode(payload)) {
        DOMService.darkModeOn();
    } else {
        DOMService.darkModeOff();
    }

    yield put(
        gtmTrack({
            action: payload,
            event: analyticsEvents.THEME_TOGGLE,
        }),
    );
}

// CHANNELS

function onlineStatusChannels() {
    return eventChannel(emitter => {
        window.addEventListener('online', emitter);
        window.addEventListener('offline', emitter);

        return () => {
            window.removeEventListener('online', emitter);
            window.removeEventListener('offline', emitter);
        };
    });
}

function preferredColorSchemeChannel() {
    return eventChannel(emitter => {
        const mql = DOMService.registerMqlEventListener(colorScheme => emitter(colorScheme));
        return () => DOMService.removeMqlEventListener(mql);
    });
}

// WATCHERS
function* watchUpdateSortingSettings() {
    yield takeLatest(ActionTypes.UI_DASHBOARD_SORTING_SETTINGS_UPDATED, updateSortingSettingsSaga);
}

function* watchSelectedKeywordIdsSelectAllRequests() {
    yield takeEvery(ActionTypes.UI_DASHBOARD_SELECTED_KEYWORD_IDS_SELECT_ALL_REQUESTED, selectAllKeywords);
}

function* watchOnlineStatusByEvents() {
    const channel = yield call(onlineStatusChannels);
    yield takeEvery(channel, updateOnlineStatus);
}

function* watchPreferredColorSchemeByEvents() {
    const channel = yield call(preferredColorSchemeChannel);
    yield takeEvery(channel, updateColorSchemeFromChannel);
}

function* watchPreferredColorSchemeChange() {
    yield takeEvery(ActionTypes.UI_MISC_COLOR_SCHEME_SET, updateColorScheme);
}

export function* watchUIRequests() {
    yield spawn(watchUpdateSortingSettings);
    yield spawn(watchOnlineStatusByEvents);
    yield spawn(watchSelectedKeywordIdsSelectAllRequests);
    yield spawn(watchPreferredColorSchemeByEvents);
    yield spawn(watchPreferredColorSchemeChange);
}
